/**
 * Library data for the `/library` route (web/docs/data-layer.md). The user's library
 * is backed by `GET /v1/home` — exactly what the mobile `getLibrary` does
 * (`app/src/data/api-real.ts`): the `watching` key carries the user's most
 * recently updated non-removed titles (any status, shows and movies) with
 * status/progress/rating inline, so the shelf renders with no per-title calls.
 *
 * `/v1/home` pages at 20 rows per call (docs/api-schema.md GET /v1/home);
 * `useLibrary` is an infinite query that keeps fetching pages via the
 * response's `cursor` until the backend stops returning one, so a library
 * over 20 titles is fully reachable — not just its most-recently-updated 20.
 *
 * Filter, type, sort and view stay derived client state over the FLATTENED
 * page list — one infinite-query cache entry (`['home']`, 60s stale), no
 * refetch when a lens changes. A `404` (endpoint the backend has not shipped)
 * degrades to an empty library, the same contract as the app.
 */

import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, type InfiniteData, type QueryClient } from '@tanstack/react-query';

import { apiGet, ApiError } from '@/api/http';
import type { ApiHome, ApiHomeShow } from '@shared/api-types';
import { mapHomeShowToTitle } from '@shared/mappers';
import type { LibraryItem } from '@shared/types';

export const LIBRARY_QUERY_KEY = ['home'] as const;

function homeShowsToLibraryItems(shows: (ApiHomeShow | null | undefined)[]): LibraryItem[] {
  return shows
    .filter((s): s is ApiHomeShow => Boolean(s))
    .map((s) => {
      const title = mapHomeShowToTitle(s);
      return {
        title,
        status: title.userStatus ?? 'watching',
        userRating: title.userRating ?? null,
        watchedCount: Math.round(s.watched_episodes ?? 0),
        totalEpisodes: Math.round(s.total_episodes ?? s.number_of_episodes ?? 0),
        rewatchCount: title.rewatchCount ?? 0,
      };
    })
    .filter((it) => it.status !== 'removed');
}

async function fetchLibraryPage(cursor?: string, perPage?: number): Promise<ApiHome> {
  const query: Record<string, string> = {};
  if (cursor) query.cursor = cursor;
  if (perPage) query.per_page = String(perPage);
  try {
    return await apiGet<ApiHome>('/v1/home', Object.keys(query).length ? { query } : undefined);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { watching: [], popular: [] };
    throw err;
  }
}

/**
 * The shelf, paginated to the whole library. Data comes from the query cache
 * (PageTransition re-serves cached route elements, so the screen must not
 * rely on a fresh mount — see web/docs/data-layer.md).
 */
export function useLibrary() {
  return useInfiniteQuery({
    queryKey: LIBRARY_QUERY_KEY,
    queryFn: ({ pageParam }: { pageParam?: string }) => fetchLibraryPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    select: (data) => ({
      pages: data.pages,
      items: homeShowsToLibraryItems(data.pages.flatMap((page) => page.watching ?? [])),
    }),
  });
}

/** `/v1/home`'s maximum page size, and a hard stop for the whole-library read. */
const WHOLE_LIBRARY_PAGE_SIZE = 100;
const WHOLE_LIBRARY_MAX_PAGES = 10;

/**
 * The WHOLE library in one cached read (`['home', 'all']`), for surfaces that
 * must see every title regardless of recency — Up next needs every series in
 * progress, not just those in the first 20-row page the shelf has loaded, and
 * the shelf count needs every row. `/v1/home` has no status filter, so this is
 * an infinite query at the maximum page size that fetches the next page by
 * itself until the cursor runs out (a 150-title library is 2 requests),
 * capped at 10 pages.
 *
 * Progressive (ui-ux.md §7 "Render progressively"): `data.items` holds the
 * rows loaded SO FAR — most recently updated first — so a caller renders from
 * the first page while the rest stream in behind it. `data.isComplete` is
 * true only once the cursor ran out (never at the cap: an exact count needs
 * every row). Shares the `['home']` prefix, so any invalidation of the shelf
 * after a watch/status mutation refreshes it too.
 */
export function useWholeLibrary() {
  const query = useInfiniteQuery({
    queryKey: [...LIBRARY_QUERY_KEY, 'all'] as const,
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      fetchLibraryPage(pageParam, WHOLE_LIBRARY_PAGE_SIZE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage, pages) =>
      pages.length < WHOLE_LIBRARY_MAX_PAGES ? lastPage.cursor : undefined,
    select: (data) => ({
      items: homeShowsToLibraryItems(data.pages.flatMap((page) => page.watching ?? [])),
      isComplete: !data.pages[data.pages.length - 1]?.cursor,
    }),
    staleTime: 60_000,
  });

  // Keep walking the cursor in the background. Every caller runs this, so
  // `cancelRefetch: false` makes the second caller join the in-flight page
  // instead of restarting it; a failed page stops the walk until a refetch.
  // `data` (a new object per page) re-runs it: a fast page can land in the
  // same notify batch as its own `isFetching: true`, leaving the flags unchanged.
  const { hasNextPage, isFetching, isError, fetchNextPage, data } = query;
  useEffect(() => {
    if (hasNextPage && !isFetching && !isError) void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetching, isError, fetchNextPage, data]);

  return query;
}

/**
 * `titleId → LibraryItem` for the titles in the user's library snapshot, so
 * catalog surfaces (Discover, a genre grid) can overlay the same status marks
 * as the shelf. Backed by `/v1/home`'s pages already fetched into the shared
 * `['home']` cache — at least the most-recently-updated 20 titles, and more
 * once something (typically the Library shelf itself) has paged further this
 * session. A title outside what's loaded so far simply gets no overlay.
 */
export function useLibraryStatusMap(): Map<string, LibraryItem> {
  const { data } = useLibrary();
  return useMemo(() => {
    const map = new Map<string, LibraryItem>();
    for (const item of data?.items ?? []) map.set(item.title.id, item);
    return map;
  }, [data]);
}

/**
 * Optimistically patch one title's `/v1/home` row in every `['home']` cache —
 * the shelf and the whole-library read share the page shape (the cover
 * wall's quick actions). The show mutations
 * (`useSetStatus`, `useTitleRating`, `useFinishTitle`) patch the detail cache
 * and invalidate `['home']` when they settle, so a failed write is corrected
 * by that refetch — no snapshot to restore here. In-flight pages are not
 * cancelled (that would stall the shelf's pagination sentinel); the settle
 * refetch wins either way.
 */
export function patchLibraryRow(qc: QueryClient, id: string, patch: Partial<ApiHomeShow>): void {
  qc.setQueriesData<InfiniteData<ApiHome>>({ queryKey: LIBRARY_QUERY_KEY }, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            watching: (page.watching ?? []).map((row) =>
              row && String(row.tmdb_id) === id ? { ...row, ...patch } : row,
            ),
          })),
        }
      : old,
  );
}
